// src/pages/StudentDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  TextField,
  Snackbar,
  LinearProgress,
  CardActionArea,
  CardActions,
  Rating,
  Badge,
} from '@mui/material';
import {
  Assignment,
  Person,
  Message,
  Dashboard,
  CheckCircle,
  PendingActions,
  AccessTime,
  Download,
  Visibility,
  Edit,
  Refresh,
  Email,
  AssignmentTurnedIn,
  Work,
  Warning,
  DoneAll,
  Cancel,
  List as ListIcon,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  MoreVert,
  NoteAdd,
  Chat,
  RateReview,
  Star,
  StarBorder,
  StarHalf,
  PictureAsPdf,
  Description,
  History,
  Receipt,
  SupportAgent,
  Info,
  KeyboardArrowRight,
  FileCopy,
  Schedule,
  Paid,
  Grade,
  ThumbUp,
  ThumbDown,
  Send,
  Lock,
  ExpandMore,
  CloudDownload,
  AttachFile,
  CalendarToday,
  Language,
  FormatSize,
  LineStyle,
  Slideshow,
  Source,
  StarRate,
  TextSnippet,
  Notes,
  Comment,
  Feedback,
  InsertDriveFile as InsertDriveFileIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  Folder,
  Check,
  Close,
  Circle,
  ExpandCircleDown,
  ArrowRightAlt,
  Timeline,
  CreditCard,
  HourglassEmpty,
  HourglassFull,
  Speed,
  Alarm,
  Timer,
  CalendarMonth,
  Event,
  EventAvailable,
  EventBusy,
  AccessAlarm,
  Timelapse,
  WatchLater,
  ScheduleSend,
  MarkEmailRead,
  Markunread,
  Drafts,
  Image as ImageIcon,
  Collections,
  BrokenImage,
  ImageNotSupported,
  NoPhotography,
  CameraFront,
  CameraRear,
  FlipCameraAndroid,
  FlipCameraIos,
  RotateRight,
  RotateLeft,
  Transform,
  CropRotate,
  FilterNone,
  Filter1,
  Filter2,
  Filter3,
  Filter4,
  Filter5,
  Filter6,
  Filter7,
  Filter8,
  Filter9,
  Filter9Plus,
  FilterBAndW,
  FilterCenterFocus,
  FilterDrama,
  FilterFrames,
  FilterHdr,
  FilterNone as FilterNoneIcon,
  FilterTiltShift,
  FilterVintage,
  Gradient,
  Grain,
  GridOff,
  HdrOff,
  HdrOn,
  HdrStrong,
  HdrWeak,
  Healing,
  Looks,
  Looks3,
  Looks4,
  Looks5,
  Looks6,
  LooksOne,
  LooksTwo,
  Loupe,
  MonochromePhotos,
  MusicNote,
  Nature,
  NaturePeople,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Palette,
  PanTool,
  PhotoAlbum,
  PhotoFilter,
  PhotoLibrary,
  PhotoSizeSelectActual,
  PhotoSizeSelectLarge,
  PhotoSizeSelectSmall,
  Portrait,
  RawOn,
  ReceiptLong,
  RemoveRedEye,
  Rotate90DegreesCcw,
  ShutterSpeed,
  Square,
  Style,
  SwitchCamera,
  SwitchVideo,
  TagFaces,
  Texture,
  Timelapse as TimelapseIcon,
  Timer10,
  Timer3,
  TimerOff,
  Tonality,
  TouchApp,
  Transform as TransformIcon,
  Tune,
  ViewComfy,
  ViewCompact,
  Vignette,
  WbAuto,
  WbCloudy,
  WbIncandescent,
  WbIridescent,
  WbSunny,
  ZoomOutMap,
} from '@mui/icons-material';
import { 
  getUserOrders,
  clientRequestRevision,
  rateOrder,
  getClientStats,
  getOrderDetails,
  downloadFile,
  downloadOrderFile,
} from '../api/api';
import { format } from 'date-fns';
import './StudentDashboard.css';

// Import the MessagingCenter component
import MessagingCenter from '../components/messaging/MessagingCenter';
// Import the OrderPage component for new orders
import OrderPage from './OrderPage';

const StudentDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    assignedOrders: 0,
    inProgressOrders: 0,
    submittedOrders: 0,
    completedOrders: 0,
    revisionOrders: 0,
    totalSpent: 0,
    averageRating: 0,
  });

  // Order details dialog state - UPDATED
  const [orderDetails, setOrderDetails] = useState({
    open: false,
    order: null,
    loading: false,
    error: null,
  });

  // Revision dialog state
  const [revisionDialog, setRevisionDialog] = useState({
    open: false,
    orderId: null,
    orderTitle: '',
    revisionNotes: '',
    isSubmitting: false,
  });

  // Rating dialog state
  const [ratingDialog, setRatingDialog] = useState({
    open: false,
    orderId: null,
    orderTitle: '',
    rating: 0,
    review: '',
    isSubmitting: false,
  });

  // Quick message dialog
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    recipientId: null,
    recipientName: '',
    orderId: null,
    message: '',
    isSubmitting: false,
  });

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Status tabs for orders section - Updated to include CLOSED status
  const statusTabs = [
    { label: 'All', value: 'ALL', icon: <ListIcon /> },
    { label: 'Pending', value: 'PENDING', icon: <PendingActions /> },
    { label: 'Assigned', value: 'ASSIGNED', icon: <Assignment /> },
    { label: 'In Progress', value: 'IN_PROGRESS', icon: <Work /> },
    { label: 'Submitted', value: 'SUBMITTED', icon: <AssignmentTurnedIn /> },
    { label: 'Revision', value: 'REVISION', icon: <Warning /> },
    { label: 'Completed', value: 'COMPLETED', icon: <CheckCircle /> },
    { label: 'Closed', value: 'CLOSED', icon: <Lock /> },
  ];

  const [activeStatusTab, setActiveStatusTab] = useState(0);

  const tabs = [
    { label: 'Dashboard', icon: <Dashboard /> },
    { label: 'My Orders', icon: <Assignment /> },
    { label: 'New Order', icon: <NoteAdd /> },
    { label: 'Messages', icon: <Message /> },
    { label: 'History', icon: <History /> },
  ];

  // Check user role on mount
  useEffect(() => {
    const checkUserRole = () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log("👤 Current student/client:", user);
      
      if (user.role !== 'CLIENT') {
        console.warn("⚠️ User is not a client, but viewing student dashboard");
      }
    };
    
    checkUserRole();
  }, []);

  // Fetch all data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch client's orders
      const ordersResponse = await getUserOrders();
      console.log("📋 Student orders response:", ordersResponse);
      
      // Handle different response formats
      let ordersData = [];
      if (Array.isArray(ordersResponse)) {
        ordersData = ordersResponse;
      } else if (ordersResponse.orders) {
        ordersData = ordersResponse.orders;
      } else if (ordersResponse.results) {
        ordersData = ordersResponse.results;
      } else if (ordersResponse.data) {
        ordersData = ordersResponse.data;
      }
      
      console.log("📋 Processed orders data:", ordersData);
      setOrders(ordersData);
      
      // Calculate statistics
      calculateStats(ordersData);
      
      // Try to fetch additional client stats
      try {
        const clientStats = await getClientStats();
        if (clientStats) {
          setStats(prev => ({
            ...prev,
            totalSpent: clientStats.total_spent || prev.totalSpent,
            averageRating: clientStats.average_rating || prev.averageRating,
          }));
        }
      } catch (statsError) {
        console.log("Stats endpoint not available yet, using calculated stats");
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showSnackbar('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ordersData) => {
    const totalOrders = ordersData.length;
    const pendingOrders = ordersData.filter(o => o.status === 'PENDING').length;
    const assignedOrders = ordersData.filter(o => o.status === 'ASSIGNED').length;
    const inProgressOrders = ordersData.filter(o => o.status === 'IN_PROGRESS').length;
    const submittedOrders = ordersData.filter(o => o.status === 'SUBMITTED').length;
    const completedOrders = ordersData.filter(o => o.status === 'COMPLETED').length;
    const revisionOrders = ordersData.filter(o => o.status === 'REVISION').length;
    const closedOrders = ordersData.filter(o => o.status === 'CLOSED').length;
    
    const totalSpent = ordersData
      .filter(o => o.status === 'COMPLETED' || o.status === 'CLOSED')
      .reduce((sum, order) => sum + parseFloat(order.budget || 0), 0);
    
    const ratedOrders = ordersData.filter(o => o.rating && o.rating > 0);
    const averageRating = ratedOrders.length > 0 
      ? ratedOrders.reduce((sum, order) => sum + order.rating, 0) / ratedOrders.length
      : 0;
    
    setStats({
      totalOrders,
      pendingOrders,
      assignedOrders,
      inProgressOrders,
      submittedOrders,
      completedOrders,
      revisionOrders,
      totalSpent,
      averageRating,
    });
  };

  // Enhanced file download handler
  const handleDownloadFile = async (fileUrl, fileName, orderId, fileType) => {
    try {
      console.log(`Downloading ${fileType} for order ${orderId}`);
      
      // If we have orderId and fileType, use the specific download function
      if (orderId && fileType) {
        try {
          const result = await downloadOrderFile(orderId, fileType, fileName);
          showSnackbar(`Downloading ${fileName}...`, 'info');
          return result;
        } catch (specificError) {
          console.log('Specific download failed, trying generic:', specificError);
          // Fall back to generic download
        }
      }
      
      // Use generic download function
      await downloadFile(fileUrl, fileName);
      showSnackbar(`Downloading ${fileName}...`, 'info');
    } catch (error) {
      console.error('Download error:', error);
      
      // Fallback to simple download
      try {
        if (!fileUrl) {
          showSnackbar('No file available to download', 'warning');
          return;
        }

        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${baseUrl}${fileUrl}`;
        
        // Add token for authentication
        const token = localStorage.getItem('access_token');
        const urlWithToken = token ? `${fullUrl}?token=${token}` : fullUrl;
        
        // Create and trigger download
        const link = document.createElement('a');
        link.href = urlWithToken;
        link.download = fileName || fileUrl.split('/').pop() || 'download';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        showSnackbar('Download started in new tab...', 'info');
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
        showSnackbar(`Download failed: ${error.message}`, 'error');
      }
    }
  };

  // Handle opening order details with full data fetch
  const handleOpenOrderDetails = async (order) => {
    try {
      // Show dialog immediately with basic data
      setOrderDetails({
        open: true,
        order: order, // Show basic data immediately
        loading: true,
        error: null,
      });
      
      // Fetch full order details
      const fullOrder = await getOrderDetails(order.id);
      
      // Update dialog with full data
      setOrderDetails({
        open: true,
        order: fullOrder,
        loading: false,
        error: null,
      });
      
    } catch (error) {
      console.error('Error fetching order details:', error);
      setOrderDetails({
        open: true,
        order: order, // Keep basic data
        loading: false,
        error: 'Failed to load complete order details',
      });
    }
  };

  const handleRequestRevision = async () => {
    try {
      if (!revisionDialog.orderId) {
        showSnackbar("No order selected", 'warning');
        return;
      }

      if (!revisionDialog.revisionNotes.trim()) {
        showSnackbar("Please provide revision notes", 'warning');
        return;
      }

      setRevisionDialog(prev => ({ ...prev, isSubmitting: true }));

      // Request revision using the client-specific endpoint
      const response = await clientRequestRevision(revisionDialog.orderId, revisionDialog.revisionNotes);
      
      // Refresh orders
      const ordersResponse = await getUserOrders();
      let ordersData = [];
      if (Array.isArray(ordersResponse)) {
        ordersData = ordersResponse;
      } else if (ordersResponse.orders) {
        ordersData = ordersResponse.orders;
      }
      setOrders(ordersData);
      calculateStats(ordersData);
      
      setRevisionDialog({ 
        open: false, 
        orderId: null, 
        orderTitle: '', 
        revisionNotes: '', 
        isSubmitting: false 
      });
      
      // Show success message
      if (response && response.message) {
        showSnackbar(response.message, 'success');
      } else {
        showSnackbar("Revision requested successfully! The order status is now REVISION and the writer will make the changes.", 'success');
      }
      
    } catch (error) {
      console.error('Error requesting revision:', error);
      setRevisionDialog(prev => ({ ...prev, isSubmitting: false }));
      
      if (error.response?.status === 403) {
        showSnackbar("You can only request revisions for your own orders", 'error');
      } else if (error.response?.status === 400) {
        const errorMsg = error.response.data?.error || error.message;
        showSnackbar(`Cannot request revision: ${errorMsg}`, 'error');
      } else {
        showSnackbar(`Failed to request revision: ${error.message || 'Unknown error'}`, 'error');
      }
    }
  };

  const handleSubmitRating = async () => {
    try {
      if (!ratingDialog.orderId || ratingDialog.rating === 0) {
        showSnackbar("Please provide a rating", 'warning');
        return;
      }

      setRatingDialog(prev => ({ ...prev, isSubmitting: true }));

      // Submit rating
      const response = await rateOrder(ratingDialog.orderId, ratingDialog.rating, ratingDialog.review);
      
      // Refresh orders
      const ordersResponse = await getUserOrders();
      let ordersData = [];
      if (Array.isArray(ordersResponse)) {
        ordersData = ordersResponse;
      } else if (ordersResponse.orders) {
        ordersData = ordersResponse.orders;
      }
      setOrders(ordersData);
      calculateStats(ordersData);
      
      setRatingDialog({ 
        open: false, 
        orderId: null, 
        orderTitle: '', 
        rating: 0, 
        review: '', 
        isSubmitting: false 
      });
      
      // Show success message with more details
      if (response && response.message) {
        showSnackbar(response.message, 'success');
      } else {
        showSnackbar("Thank you for your rating! The order has been marked as CLOSED. No further revisions can be requested.", 'success');
      }
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      setRatingDialog(prev => ({ ...prev, isSubmitting: false }));
      
      if (error.response?.status === 403) {
        showSnackbar("You can only rate your own orders", 'error');
      } else if (error.response?.status === 400) {
        const errorMsg = error.response.data?.error || error.message;
        showSnackbar(`Cannot rate order: ${errorMsg}`, 'error');
      } else {
        showSnackbar(`Failed to submit rating: ${error.message || 'Unknown error'}`, 'error');
      }
    }
  };

  const handleSendMessage = async () => {
    try {
      if (!messageDialog.message.trim()) {
        showSnackbar("Please enter a message", 'warning');
        return;
      }

      setMessageDialog(prev => ({ ...prev, isSubmitting: true }));

      // In a real app, you would call the sendMessage API
      // For now, we'll simulate it
      console.log(`Sending message to ${messageDialog.recipientName}:`, messageDialog.message);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setMessageDialog({ 
        open: false, 
        recipientId: null, 
        recipientName: '', 
        orderId: null, 
        message: '', 
        isSubmitting: false 
      });
      showSnackbar(`Message sent to ${messageDialog.recipientName}`, 'success');
      
    } catch (error) {
      console.error('Error sending message:', error);
      setMessageDialog(prev => ({ ...prev, isSubmitting: false }));
      showSnackbar(`Failed to send message: ${error.message}`, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'ASSIGNED': return 'info';
      case 'IN_PROGRESS': return 'primary';
      case 'SUBMITTED': return 'secondary';
      case 'COMPLETED': return 'success';
      case 'REVISION': return 'error';
      case 'CLOSED': return 'default';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING': return <PendingActions />;
      case 'ASSIGNED': return <Assignment />;
      case 'IN_PROGRESS': return <Work />;
      case 'SUBMITTED': return <AssignmentTurnedIn />;
      case 'COMPLETED': return <CheckCircle />;
      case 'REVISION': return <Warning />;
      case 'CLOSED': return <Lock />;
      default: return <AccessTime />;
    }
  };

  const getStatusClassName = (status) => {
    return status.toLowerCase().replace('_', '-');
  };

  const getProgressValue = (status) => {
    const progressMap = {
      'PENDING': 10,
      'ASSIGNED': 30,
      'IN_PROGRESS': 60,
      'SUBMITTED': 80,
      'REVISION': 70,
      'COMPLETED': 100,
      'CLOSED': 100,
      'CANCELLED': 0,
    };
    return progressMap[status] || 0;
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'CLOSED': return 'success';
      case 'REVISION': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'primary';
    }
  };

  const getTimeRemaining = (deadline) => {
    if (!deadline) return 'No deadline';
    
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffMs = deadlineDate - now;
    
    if (diffMs < 0) return 'Overdue';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    return `${diffHours}h`;
  };

  const getUrgentLevel = (deadline) => {
    if (!deadline) return 'normal';
    
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffHours = (deadlineDate - now) / (1000 * 60 * 60);
    
    if (diffHours < 24) return 'critical';
    if (diffHours < 48) return 'high';
    if (diffHours < 72) return 'medium';
    return 'normal';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <InsertDriveFileIcon />;
    
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return <PictureAsPdf />;
      case 'doc':
      case 'docx': return <Description />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <Image />;
      case 'mp4':
      case 'avi':
      case 'mov': return <VideoFile />;
      case 'mp3':
      case 'wav': return <AudioFile />;
      case 'zip':
      case 'rar': return <Archive />;
      default: return <InsertDriveFileIcon />;
    }
  };

  // Get filtered orders based on active status tab
  const getFilteredOrders = () => {
    const selectedStatus = statusTabs[activeStatusTab].value;
    
    if (selectedStatus === 'ALL') {
      return orders;
    }
    
    return orders.filter(order => order.status === selectedStatus);
  };

  // Get count for each status tab
  const getStatusCount = (status) => {
    if (status === 'ALL') {
      return orders.length;
    }
    
    return orders.filter(order => order.status === status).length;
  };

  // Check if revision is allowed for an order - ONLY for COMPLETED orders
  const isRevisionAllowed = (order) => {
    // Allow revision ONLY for COMPLETED orders that haven't been rated yet
    return order.status === 'COMPLETED' && 
           !order.rating && 
           order.status !== 'CLOSED';
  };

  // Check if rating is allowed for an order - ONLY for COMPLETED orders
  const isRatingAllowed = (order) => {
    // Allow rating ONLY for COMPLETED orders that haven't been rated yet
    return order.status === 'COMPLETED' && 
           !order.rating && 
           order.status !== 'CLOSED';
  };

  // Check if order is closed (rated)
  const isOrderClosed = (order) => {
    return order.status === 'CLOSED' || order.rating;
  };

  // Check if completed work can be downloaded
  const canDownloadCompletedWork = (order) => {
    // Completed work should be available for download only when order status is Completed or Closed
    return (order.status === 'COMPLETED' || order.status === 'CLOSED') && 
           order.completed_work;
  };

  // Render Dashboard Tab
  const renderDashboard = () => (
    <Box>
      <div className="section-header">
        <Typography variant="h5" className="section-title">
          My Dashboard
        </Typography>
        <div className="section-actions">
          <Button
            className="btn btn-outline"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
          >
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Welcome Card */}
      <Paper className="welcome-card" sx={{ mb: 3, p: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'white', color: 'primary.main' }}>
              <Person fontSize="large" />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight="bold">
              Welcome back!
            </Typography>
            <Typography variant="body1">
              Track your orders, communicate with writers, and manage your assignments.
            </Typography>
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }}
              onClick={() => setActiveTab(2)}
            >
              <NoteAdd sx={{ mr: 1 }} /> New Order
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Stats Grid */}
      <div className="stats-grid">
        {/* Total Orders */}
        <Paper className="stat-card" onClick={() => { setActiveTab(1); setActiveStatusTab(0); }}>
          <div className="stat-card-header">
            <div className="stat-icon primary">
              <Assignment />
            </div>
            <div>
              <Typography variant="subtitle2" className="stat-title">
                Total Orders
              </Typography>
              <Typography variant="h4" className="stat-value">
                {stats.totalOrders}
              </Typography>
              <div className="stat-change positive">
                <TrendingUp fontSize="small" /> Active
              </div>
            </div>
          </div>
        </Paper>

        {/* In Progress Orders */}
        <Paper className="stat-card" onClick={() => { setActiveTab(1); setActiveStatusTab(3); }}>
          <div className="stat-card-header">
            <div className="stat-icon primary">
              <Work />
            </div>
            <div>
              <Typography variant="subtitle2" className="stat-title">
                In Progress
              </Typography>
              <Typography variant="h4" className="stat-value">
                {stats.inProgressOrders}
              </Typography>
              <div className="stat-change positive">
                <TrendingUp fontSize="small" /> Being written
              </div>
            </div>
          </div>
        </Paper>

        {/* Submitted Orders */}
        <Paper className="stat-card" onClick={() => { setActiveTab(1); setActiveStatusTab(4); }}>
          <div className="stat-card-header">
            <div className="stat-icon secondary">
              <AssignmentTurnedIn />
            </div>
            <div>
              <Typography variant="subtitle2" className="stat-title">
                Submitted
              </Typography>
              <Typography variant="h4" className="stat-value">
                {stats.submittedOrders}
              </Typography>
              <div className="stat-change positive">
                <TrendingUp fontSize="small" /> Ready for review
              </div>
            </div>
          </div>
        </Paper>

        {/* Revision Orders */}
        <Paper className="stat-card" onClick={() => { setActiveTab(1); setActiveStatusTab(5); }}>
          <div className="stat-card-header">
            <div className="stat-icon error">
              <Warning />
            </div>
            <div>
              <Typography variant="subtitle2" className="stat-title">
                Revision
              </Typography>
              <Typography variant="h4" className="stat-value">
                {stats.revisionOrders}
              </Typography>
              <div className="stat-change negative">
                <TrendingDown fontSize="small" /> Needs revision
              </div>
            </div>
          </div>
        </Paper>

        {/* Completed Orders */}
        <Paper className="stat-card" onClick={() => { setActiveTab(1); setActiveStatusTab(6); }}>
          <div className="stat-card-header">
            <div className="stat-icon success">
              <CheckCircle />
            </div>
            <div>
              <Typography variant="subtitle2" className="stat-title">
                Completed
              </Typography>
              <Typography variant="h4" className="stat-value">
                {stats.completedOrders}
              </Typography>
              <div className="stat-change positive">
                <TrendingUp fontSize="small" /> Ready to use
              </div>
            </div>
          </div>
        </Paper>

        {/* Total Spent */}
        <Paper className="stat-card">
          <div className="stat-card-header">
            <div className="stat-icon revenue">
              <AttachMoney />
            </div>
            <div>
              <Typography variant="subtitle2" className="stat-title">
                Total Spent
              </Typography>
              <Typography variant="h4" className="stat-value">
                ${stats.totalSpent.toFixed(2)}
              </Typography>
              <div className="stat-change positive">
                <TrendingUp fontSize="small" /> Investment
              </div>
            </div>
          </div>
        </Paper>
      </div>

      {/* Recent Activity & Quick Actions */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <Card className="data-table-container">
            <CardContent>
              <div className="section-header">
                <Typography variant="h6" className="section-title">
                  Recent Orders
                </Typography>
                <Button 
                  className="btn btn-outline"
                  size="small"
                  onClick={() => setActiveTab(1)}
                >
                  View All
                </Button>
              </div>
              <TableContainer>
                <Table className="data-table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Writer</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.slice(0, 5).map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            #{order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {order.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            className={`status-badge ${getStatusClassName(order.status)}`}
                            icon={getStatusIcon(order.status)}
                            label={order.status === 'SUBMITTED' ? 'Under Review' : order.status}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.writer_username || 'Not assigned'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <div className={`time-display ${getUrgentLevel(order.deadline)}`}>
                            <AccessTime fontSize="small" />
                            {getTimeRemaining(order.deadline)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton
                              className="btn-icon"
                              size="small"
                              onClick={() => handleOpenOrderDetails(order)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {orders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="textSecondary">
                            No orders yet. Create your first order!
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <List>
                <ListItem 
                  button 
                  onClick={() => setActiveTab(2)}
                  sx={{ borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <NoteAdd />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="New Order" 
                    secondary="Place a new assignment order" 
                  />
                </ListItem>
                
                <ListItem 
                  button 
                  onClick={() => setActiveTab(3)}
                  sx={{ borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'info.light' }}>
                      <Message />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Messages" 
                    secondary="Check your messages" 
                  />
                </ListItem>
                
                <ListItem 
                  button 
                  onClick={() => {
                    const completedOrders = orders.filter(o => isRatingAllowed(o));
                    if (completedOrders.length > 0) {
                      setActiveTab(1);
                      setActiveStatusTab(6);
                    } else {
                      showSnackbar('No orders available for rating yet', 'info');
                    }
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.light' }}>
                      <RateReview />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Rate Orders" 
                    secondary="Review completed work" 
                  />
                </ListItem>
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Need Help?
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Contact support or check our FAQ for assistance with your orders.
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<Chat />}
                fullWidth
              >
                Contact Support
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Render My Orders Tab
  const renderMyOrders = () => {
    const filteredOrders = getFilteredOrders();
    const selectedStatus = statusTabs[activeStatusTab].value;
    
    return (
      <Box>
        <div className="section-header">
          <Typography variant="h5" className="section-title">
            My Orders
          </Typography>
          <div className="section-actions">
            <Button
              className="btn btn-outline"
              startIcon={<Refresh />}
              onClick={fetchDashboardData}
            >
              Refresh
            </Button>
            <Button
              className="btn btn-primary"
              startIcon={<NoteAdd />}
              onClick={() => setActiveTab(2)}
            >
              New Order
            </Button>
          </div>
        </div>

        {/* Status Tabs for Orders */}
        <Paper className="admin-tabs-container" sx={{ mb: 3 }}>
          <Tabs
            value={activeStatusTab}
            onChange={(e, newValue) => setActiveStatusTab(newValue)}
            className="admin-tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {statusTabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={`${tab.label} (${getStatusCount(tab.value)})`}
                className={`admin-tab ${activeStatusTab === index ? 'active' : ''}`}
              />
            ))}
          </Tabs>
          
          <Box sx={{ p: 2 }}>
            {/* Orders Grid View */}
            {filteredOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <div className="empty-state-icon" style={{ fontSize: '4rem', color: '#ccc', marginBottom: '1rem' }}>
                  <Assignment fontSize="inherit" />
                </div>
                <Typography variant="h6" className="empty-state-title">
                  No orders found
                </Typography>
                <Typography variant="body2" className="empty-state-description" sx={{ mb: 3 }}>
                  {selectedStatus === 'ALL' 
                    ? "You haven't placed any orders yet."
                    : `No orders with status: ${selectedStatus}`
                  }
                </Typography>
                <Button 
                  className="btn btn-primary"
                  onClick={() => setActiveTab(2)}
                >
                  Place Your First Order
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {filteredOrders.map((order) => {
                  const canRate = isRatingAllowed(order);
                  const canRevise = isRevisionAllowed(order);
                  const isClosed = isOrderClosed(order);
                  const canDownload = canDownloadCompletedWork(order);
                  
                  return (
                    <Grid item xs={12} md={6} lg={4} key={order.id}>
                      <Card className="order-card">
                        <CardActionArea onClick={() => handleOpenOrderDetails(order)}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
                                {order.title}
                              </Typography>
                              <Chip
                                className={`status-badge ${getStatusClassName(order.status)}`}
                                label={order.status === 'SUBMITTED' ? 'Under Review' : order.status}
                                size="small"
                              />
                            </Box>
                            
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              Order ID: #{order.id}
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" gutterBottom>
                                <strong>Writer:</strong> {order.writer_username || 'Not assigned'}
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                <strong>Pages:</strong> {order.pages} • <strong>Budget:</strong> ${parseFloat(order.budget || 0).toFixed(2)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Deadline:</strong> {getTimeRemaining(order.deadline)}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" gutterBottom>
                                Progress:
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={getProgressValue(order.status)} 
                                color={getProgressColor(order.status)}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                                {getProgressValue(order.status)}%
                              </Typography>
                            </Box>
                            
                            {order.status === 'SUBMITTED' && (
                              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                                <Typography variant="body2">
                                  <strong>Order Under Review:</strong> Writer has submitted work. Waiting for admin approval.
                                </Typography>
                              </Alert>
                            )}
                            
                            {order.completed_work && order.status !== 'COMPLETED' && order.status !== 'CLOSED' && (
                              <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
                                <Typography variant="body2">
                                  <strong>Work Under Review:</strong> Download not available until order is completed.
                                </Typography>
                              </Alert>
                            )}
                            
                            {order.revision_count > 0 && (
                              <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
                                <Typography variant="body2">
                                  Revision #{order.revision_count} requested
                                </Typography>
                              </Alert>
                            )}
                            
                            {order.rating && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Rating value={order.rating} readOnly size="small" />
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                  {order.rating}/5
                                </Typography>
                              </Box>
                            )}
                            
                            {isClosed && (
                              <Alert severity="success" sx={{ mt: 2, py: 0.5 }}>
                                <Typography variant="body2">
                                  Order closed and rated
                                </Typography>
                              </Alert>
                            )}
                            
                            {canRate && (
                              <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                                <Typography variant="body2">
                                  <strong>Action Required:</strong> Please rate this order to close it.
                                </Typography>
                              </Alert>
                            )}
                          </CardContent>
                        </CardActionArea>
                        
                        <CardActions>
                          <Button 
                            size="small" 
                            startIcon={<Visibility />}
                            onClick={() => handleOpenOrderDetails(order)}
                          >
                            Details
                          </Button>
                          
                          {canDownload && (
                            <Button 
                              size="small" 
                              startIcon={<Download />}
                              onClick={() => handleDownloadFile(
                                order.completed_work, 
                                `order_${order.id}_completed_work`,
                                order.id,
                                'completed_work'
                              )}
                            >
                              Download
                            </Button>
                          )}
                          
                          {canRate && (
                            <Button 
                              size="small" 
                              startIcon={<RateReview />}
                              onClick={() => setRatingDialog({
                                open: true,
                                orderId: order.id,
                                orderTitle: order.title,
                                rating: 0,
                                review: '',
                                isSubmitting: false,
                              })}
                            >
                              Rate
                            </Button>
                          )}
                          
                          {canRevise && (
                            <Button 
                              size="small" 
                              startIcon={<Warning />}
                              color="warning"
                              onClick={() => setRevisionDialog({
                                open: true,
                                orderId: order.id,
                                orderTitle: order.title,
                                revisionNotes: order.revision_notes || '',
                                isSubmitting: false,
                              })}
                            >
                              Revision
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  // Render New Order Tab - Using OrderPage component
  const renderNewOrder = () => (
    <Box>
      <div className="section-header">
        <Typography variant="h5" className="section-title">
          Place New Order
        </Typography>
        <div className="section-actions">
          <Button
            className="btn btn-outline"
            startIcon={<History />}
            onClick={() => setActiveTab(4)}
          >
            View History
          </Button>
          <Button
            className="btn btn-outline"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
          >
            Refresh Orders
          </Button>
        </div>
      </div>
      
      {/* Use the OrderPage component */}
      <OrderPage isEmbedded={true} onOrderCreated={fetchDashboardData} />
    </Box>
  );

  // Render Messages Tab
  const renderMessages = () => (
    <Box sx={{ width: '100%' }}>
      <div className="section-header">
        <Typography variant="h5" className="section-title">
          <Message sx={{ mr: 1 }} /> Messaging Center
        </Typography>
        <div className="section-actions">
          <Button 
            className="btn btn-outline"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
          >
            Refresh
          </Button>
        </div>
      </div>
      
      <Paper className="admin-content-panel messaging-panel">
        <MessagingCenter />
      </Paper>
    </Box>
  );

  // Render History Tab
  const renderHistory = () => {
    const completedOrders = orders.filter(order => order.status === 'COMPLETED' || order.status === 'CLOSED');
    const cancelledOrders = orders.filter(order => order.status === 'CANCELLED');
    
    return (
      <Box>
        <div className="section-header">
          <Typography variant="h5" className="section-title">
            <History sx={{ mr: 1 }} /> Order History
          </Typography>
          <div className="section-actions">
            <Button
              className="btn btn-outline"
              startIcon={<Refresh />}
              onClick={fetchDashboardData}
            >
              Refresh
            </Button>
          </div>
        </div>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ mr: 1, color: 'success.main' }} /> Completed & Closed Orders
                  <Chip label={completedOrders.length} size="small" sx={{ ml: 'auto' }} />
                </Typography>
                <List>
                  {completedOrders.slice(0, 5).map((order) => (
                    <ListItem 
                      key={order.id}
                      secondaryAction={
                        <IconButton edge="end" onClick={() => handleOpenOrderDetails(order)}>
                          <Visibility />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: order.status === 'CLOSED' ? 'default' : 'success.light' }}>
                          {order.status === 'CLOSED' ? <Lock /> : <Assignment />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={order.title}
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              {order.status === 'CLOSED' ? 'Closed' : 'Completed'}: {formatDateShort(order.completed_at || order.updated_at)}
                            </Typography>
                            <br />
                            {order.rating ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Rating value={order.rating} readOnly size="small" />
                                <Typography variant="caption" sx={{ ml: 1 }}>
                                  {order.rating}/5
                                </Typography>
                              </Box>
                            ) : order.status === 'COMPLETED' ? (
                              <Button 
                                size="small" 
                                startIcon={<RateReview />}
                                onClick={() => setRatingDialog({
                                  open: true,
                                  orderId: order.id,
                                  orderTitle: order.title,
                                  rating: 0,
                                  review: '',
                                  isSubmitting: false,
                                })}
                              >
                                Rate Now
                              </Button>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                Not rated yet
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                  {completedOrders.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No completed orders yet"
                        secondary="Your completed orders will appear here"
                      />
                    </ListItem>
                  )}
                </List>
                {completedOrders.length > 5 && (
                  <Button fullWidth onClick={() => { setActiveTab(1); setActiveStatusTab(6); }}>
                    View All Completed Orders
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Cancel sx={{ mr: 1, color: 'error.main' }} /> Cancelled Orders
                  <Chip label={cancelledOrders.length} size="small" sx={{ ml: 'auto' }} />
                </Typography>
                <List>
                  {cancelledOrders.slice(0, 5).map((order) => (
                    <ListItem 
                      key={order.id}
                      secondaryAction={
                        <IconButton edge="end" onClick={() => handleOpenOrderDetails(order)}>
                          <Visibility />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'error.light' }}>
                          <Cancel />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={order.title}
                        secondary={`Cancelled: ${formatDateShort(order.updated_at)}`}
                      />
                    </ListItem>
                  ))}
                  {cancelledOrders.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No cancelled orders"
                        secondary="You haven't cancelled any orders"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {stats.totalOrders}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Orders
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {stats.completedOrders + orders.filter(o => o.status === 'CLOSED').length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Completed/Closed
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {stats.revisionOrders}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Revisions
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">
                        ${stats.totalSpent.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Spent
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Enhanced Order Details Dialog with ALL database fields (Student version)
  const renderOrderDetailsDialog = () => {
    if (!orderDetails.order) return null;
    
    const order = orderDetails.order;
    const canRequestRevision = isRevisionAllowed(order);
    const canRateOrder = isRatingAllowed(order);
    const isClosed = isOrderClosed(order);
    const canDownloadCompletedWork = (order.status === 'COMPLETED' || order.status === 'CLOSED') && order.completed_work;
    
    return (
      <Dialog
        open={orderDetails.open}
        onClose={() => setOrderDetails({ ...orderDetails, open: false })}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: 'admin-dialog large-dialog',
          sx: { borderRadius: 12 }
        }}
      >
        <div className="dialog-header" style={{ 
          padding: '20px 24px 12px', 
          borderBottom: '1px solid #e5e7eb', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Typography variant="h6" style={{ 
            color: '#1e293b', 
            fontWeight: 600, 
            fontSize: '18px', 
            margin: 0,
            display: 'flex',
            alignItems: 'center'
          }}>
            <Assignment sx={{ mr: 1 }} />
            Order #{order.id}: {order.title}
          </Typography>
          <IconButton 
            size="small"
            onClick={() => setOrderDetails({ ...orderDetails, open: false })}
          >
            <Cancel />
          </IconButton>
        </div>
        
        <DialogContent dividers>
          {orderDetails.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading order details...</Typography>
            </Box>
          ) : orderDetails.error ? (
            <Alert severity="error">{orderDetails.error}</Alert>
          ) : (
            <>
              {/* Order Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Order Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getProgressValue(order.status)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={getProgressValue(order.status)} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              <Grid container spacing={3}>
                {/* Left Column - Order Information */}
                <Grid item xs={12} md={6}>
                  {/* Order Status & Basic Info */}
                  <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <Assignment fontSize="small" sx={{ mr: 1 }} />
                      Order Status & Basic Info
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Status:</Typography>
                        <Chip
                          className={`status-badge ${getStatusClassName(order.status)}`}
                          icon={getStatusIcon(order.status)}
                          label={order.status === 'SUBMITTED' ? 'Under Review' : order.status}
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Revision Count:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.revision_count || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Order ID:</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          #{order.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Budget:</Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary" sx={{ mt: 0.5 }}>
                          ${parseFloat(order.budget || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Order Specifications */}
                  <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <Description fontSize="small" sx={{ mr: 1 }} />
                      Order Specifications
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Subject:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.subject || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Academic Level:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.academic_level || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Pages:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.pages}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Word Count (est.):</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {(order.pages || 0) * 275} words
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Language:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.language || 'English'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Format:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.format || 'APA'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Spacing:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.spacing || 'Double'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Slides:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.slides || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Sources:</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {order.sources || 0}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Additional Services */}
                  <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <AttachMoney fontSize="small" sx={{ mr: 1 }} />
                      Additional Services
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Chip
                          size="small"
                          icon={order.plagiarism_report ? <Check /> : <Close />}
                          label="Plagiarism Report"
                          color={order.plagiarism_report ? "success" : "default"}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip
                          size="small"
                          icon={order.executive_summary ? <Check /> : <Close />}
                          label="Executive Summary"
                          color={order.executive_summary ? "success" : "default"}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip
                          size="small"
                          icon={order.expert_writer ? <Check /> : <Close />}
                          label="Expert Writer"
                          color={order.expert_writer ? "success" : "default"}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Right Column - Timelines & Files */}
                <Grid item xs={12} md={6}>
                  {/* Timelines */}
                  <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <Schedule fontSize="small" sx={{ mr: 1 }} />
                      Timelines
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Created At:</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {formatDate(order.created_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Last Updated:</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {formatDate(order.updated_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Deadline:</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2">
                            {formatDate(order.deadline)}
                          </Typography>
                          <Chip
                            size="small"
                            label={getTimeRemaining(order.deadline)}
                            className={`time-display ${getUrgentLevel(order.deadline)}`}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </Grid>
                      {order.completed_at && (
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Completed At:</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {formatDate(order.completed_at)}
                          </Typography>
                        </Grid>
                      )}
                      {order.revision_requested_at && (
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Revision Requested:</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {formatDate(order.revision_requested_at)}
                          </Typography>
                        </Grid>
                      )}
                      {order.rated_at && (
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Rated At:</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {formatDate(order.rated_at)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {/* Files Section */}
                  <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <Folder fontSize="small" sx={{ mr: 1 }} />
                      Files & Attachments
                    </Typography>
                    
                    {/* Client Uploaded Files - ALWAYS accessible */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        <AttachFile fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Your Uploaded Files:
                      </Typography>
                      {order.files ? (
                        <Button
                          variant="outlined"
                          startIcon={getFileIcon(order.files)}
                          fullWidth
                          onClick={() => handleDownloadFile(
                            order.files, 
                            `order_${order.id}_client_files`,
                            order.id,
                            'client_files'
                          )}
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          Download Your Files
                        </Button>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No files uploaded by you
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Writer Submitted Work - Conditionally accessible */}
                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        <FileCopy fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Writer Submitted Work:
                      </Typography>
                      {order.completed_work ? (
                        <>
                          {canDownloadCompletedWork ? (
                            <Button
                              variant="contained"
                              startIcon={<CloudDownload />}
                              fullWidth
                              onClick={() => handleDownloadFile(
                                order.completed_work, 
                                `order_${order.id}_completed_work`,
                                order.id,
                                'completed_work'
                              )}
                              sx={{ justifyContent: 'flex-start', backgroundColor: '#08CB00', '&:hover': { backgroundColor: '#07B300' } }}
                            >
                              Download Completed Work
                            </Button>
                          ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                <strong>Work Under Review:</strong> The writer has submitted work. Download will be available once the order is marked as COMPLETED by admin.
                              </Typography>
                            </Alert>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No work submitted by writer yet
                        </Typography>
                      )}
                    </Box>
                  </Paper>

                  {/* Rating & Review (Only show if rated) */}
                  {order.rating && (
                    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <Star fontSize="small" sx={{ mr: 1 }} />
                        Your Rating & Review
                      </Typography>
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }} gutterBottom>
                          Rating:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              sx={{
                                color: i < order.rating ? '#ffc107' : '#e0e0e0',
                                fontSize: '1.5rem',
                              }}
                            />
                          ))}
                          <Typography variant="body2" sx={{ ml: 2, fontWeight: 'bold' }}>
                            {order.rating}/5
                          </Typography>
                        </Box>
                        {order.client_review && (
                          <>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }} gutterBottom>
                              Your Review:
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2">
                                {order.client_review}
                              </Typography>
                            </Paper>
                          </>
                        )}
                      </Box>
                    </Paper>
                  )}
                </Grid>

                {/* Full Width Sections */}
                <Grid item xs={12}>
                  {/* Instructions */}
                  <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <Notes fontSize="small" sx={{ mr: 1 }} />
                      Instructions
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, minHeight: 100 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {order.instructions || 'No instructions provided'}
                      </Typography>
                    </Paper>
                  </Paper>
                </Grid>

                {/* Writer Information */}
                {order.writer_username && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <Person fontSize="small" sx={{ mr: 1 }} />
                        Writer Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>Assigned Writer:</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                              {order.writer_username?.[0]?.toUpperCase() || 'W'}
                            </Avatar>
                            <Box>
                              <Typography variant="body1">{order.writer_username}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {order.writer_email || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* Revision Notes */}
                {order.revision_notes && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <Feedback fontSize="small" sx={{ mr: 1 }} />
                        Revision Notes
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="warning.dark">
                          {order.revision_notes}
                        </Typography>
                      </Paper>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        
        <div className="dialog-footer" style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #e5e7eb', 
          display: 'flex', 
          gap: 2, 
          justifyContent: 'flex-end' 
        }}>
          <Button 
            className="btn btn-outline"
            onClick={() => setOrderDetails({ ...orderDetails, open: false })}
          >
            Close
          </Button>
          
          {canRequestRevision && !isClosed && (
            <Button
              className="btn btn-warning"
              startIcon={<Warning />}
              onClick={() => {
                setOrderDetails({ ...orderDetails, open: false });
                setTimeout(() => {
                  setRevisionDialog({
                    open: true,
                    orderId: order.id,
                    orderTitle: order.title,
                    revisionNotes: order.revision_notes || '',
                    isSubmitting: false,
                  });
                }, 100);
              }}
            >
              Request Revision
            </Button>
          )}
          
          {canRateOrder && !isClosed && (
            <Button
              className="btn btn-primary"
              startIcon={<Star />}
              onClick={() => {
                setOrderDetails({ ...orderDetails, open: false });
                setTimeout(() => {
                  setRatingDialog({
                    open: true,
                    orderId: order.id,
                    orderTitle: order.title,
                    rating: 0,
                    review: '',
                    isSubmitting: false,
                  });
                }, 100);
              }}
            >
              Rate Order
            </Button>
          )}
          
          {canDownloadCompletedWork && (
            <Button
              className="btn btn-primary"
              startIcon={<Download />}
              onClick={() => handleDownloadFile(
                order.completed_work, 
                `order_${order.id}_completed_work`,
                order.id,
                'completed_work'
              )}
            >
              Download Work
            </Button>
          )}
          
          {order.files && (
            <Button
              className="btn btn-outline"
              startIcon={<Download />}
              onClick={() => handleDownloadFile(
                order.files, 
                `order_${order.id}_client_files`,
                order.id,
                'client_files'
              )}
            >
              Download Your Files
            </Button>
          )}
        </div>
      </Dialog>
    );
  };

  // Revision Dialog
  const renderRevisionDialog = () => (
    <Dialog
      open={revisionDialog.open}
      onClose={() => !revisionDialog.isSubmitting && setRevisionDialog({ ...revisionDialog, open: false })}
      PaperProps={{
        className: 'admin-dialog'
      }}
      maxWidth="sm"
      fullWidth
    >
      <div className="dialog-header">
        <Typography variant="h6" className="dialog-title">
          <Warning /> Request Revision for Order #{revisionDialog.orderId}
        </Typography>
        {!revisionDialog.isSubmitting && (
          <IconButton 
            className="dialog-close"
            onClick={() => setRevisionDialog({ ...revisionDialog, open: false })}
          >
            <Cancel />
          </IconButton>
        )}
      </div>
      
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {revisionDialog.orderTitle}
        </Typography>
        
        <div className="form-group">
          <Typography className="form-label" sx={{ mb: 1 }}>
            What needs to be revised? *
          </Typography>
          <TextField
            multiline
            rows={6}
            fullWidth
            placeholder="Please provide specific feedback on what needs to be changed or improved. Be as detailed as possible to help the writer understand your requirements."
            value={revisionDialog.revisionNotes}
            onChange={(e) => setRevisionDialog({ ...revisionDialog, revisionNotes: e.target.value })}
            className="text-field"
            disabled={revisionDialog.isSubmitting}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            Required field. Provide clear instructions for the writer.
          </Typography>
        </div>
        
        <Alert severity="info" className="alert alert-info">
          <Typography variant="body2">
            <strong>Note:</strong> Once you request revision, the order status will change to "REVISION" and will be sent back to the writer. The writer will make the changes and resubmit the work for your review.
          </Typography>
        </Alert>
        
        <Alert severity="warning" className="alert alert-warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Important:</strong> You can only request revisions for COMPLETED orders. Once you rate the order, it becomes CLOSED and you cannot request further revisions.
          </Typography>
        </Alert>
      </DialogContent>
      
      <div className="dialog-footer">
        <Button 
          className="btn btn-outline"
          onClick={() => setRevisionDialog({ ...revisionDialog, open: false })}
          disabled={revisionDialog.isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          className="btn btn-warning"
          onClick={handleRequestRevision}
          disabled={!revisionDialog.revisionNotes.trim() || revisionDialog.isSubmitting}
          startIcon={revisionDialog.isSubmitting ? <CircularProgress size={20} /> : <Warning />}
        >
          {revisionDialog.isSubmitting ? 'Submitting...' : 'Submit Revision Request'}
        </Button>
      </div>
    </Dialog>
  );

  // Rating Dialog
  const renderRatingDialog = () => (
    <Dialog
      open={ratingDialog.open}
      onClose={() => !ratingDialog.isSubmitting && setRatingDialog({ ...ratingDialog, open: false })}
      PaperProps={{
        className: 'admin-dialog'
      }}
      maxWidth="sm"
      fullWidth
    >
      <div className="dialog-header">
        <Typography variant="h6" className="dialog-title">
          <Star /> Rate Order #{ratingDialog.orderId}
        </Typography>
        {!ratingDialog.isSubmitting && (
          <IconButton 
            className="dialog-close"
            onClick={() => setRatingDialog({ ...ratingDialog, open: false })}
          >
            <Cancel />
          </IconButton>
        )}
      </div>
      
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {ratingDialog.orderTitle}
        </Typography>
        
        <div className="form-group" style={{ textAlign: 'center', margin: '20px 0' }}>
          <Typography className="form-label" sx={{ mb: 2 }}>
            How would you rate this work? *
          </Typography>
          <Rating
            name="order-rating"
            value={ratingDialog.rating}
            onChange={(event, newValue) => {
              setRatingDialog({ ...ratingDialog, rating: newValue });
            }}
            size="large"
            sx={{ fontSize: '3rem' }}
            disabled={ratingDialog.isSubmitting}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {ratingDialog.rating === 0 ? 'Select a rating' : `${ratingDialog.rating} out of 5 stars`}
          </Typography>
        </div>
        
        <div className="form-group">
          <Typography className="form-label" sx={{ mb: 1 }}>
            Your Review (Optional):
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Share your experience with this writer. What did you like? Any suggestions for improvement?"
            value={ratingDialog.review}
            onChange={(e) => setRatingDialog({ ...ratingDialog, review: e.target.value })}
            className="text-field"
            disabled={ratingDialog.isSubmitting}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            Your feedback helps us improve our service and assists other students.
          </Typography>
        </div>
        
        <Alert severity="info" className="alert alert-info">
          <Typography variant="body2">
            <strong>Important:</strong> Once you submit your rating, this order will be marked as CLOSED and you cannot request further revisions. Please ensure you're completely satisfied with the work before rating.
          </Typography>
        </Alert>
        
        <Alert severity="success" className="alert alert-success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Your rating will be submitted and the order will be automatically marked as CLOSED. You'll receive a confirmation message.
          </Typography>
        </Alert>
      </DialogContent>
      
      <div className="dialog-footer">
        <Button 
          className="btn btn-outline"
          onClick={() => setRatingDialog({ ...ratingDialog, open: false })}
          disabled={ratingDialog.isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          className="btn btn-primary"
          onClick={handleSubmitRating}
          disabled={ratingDialog.rating === 0 || ratingDialog.isSubmitting}
          startIcon={ratingDialog.isSubmitting ? <CircularProgress size={20} /> : <Star />}
        >
          {ratingDialog.isSubmitting ? 'Submitting...' : 'Submit Rating & Close Order'}
        </Button>
      </div>
    </Dialog>
  );

  // Message Dialog
  const renderMessageDialog = () => (
    <Dialog
      open={messageDialog.open}
      onClose={() => !messageDialog.isSubmitting && setMessageDialog({ ...messageDialog, open: false })}
      PaperProps={{
        className: 'admin-dialog'
      }}
      maxWidth="sm"
      fullWidth
    >
      <div className="dialog-header">
        <Typography variant="h6" className="dialog-title">
          <Message /> Message to {messageDialog.recipientName}
        </Typography>
        {!messageDialog.isSubmitting && (
          <IconButton 
            className="dialog-close"
            onClick={() => setMessageDialog({ ...messageDialog, open: false })}
          >
            <Cancel />
          </IconButton>
        )}
      </div>
      
      <DialogContent>
        {messageDialog.orderId && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Regarding Order #{messageDialog.orderId}
          </Typography>
        )}
        
        <div className="form-group">
          <Typography className="form-label" sx={{ mb: 1 }}>
            Your Message *
          </Typography>
          <TextField
            multiline
            rows={6}
            fullWidth
            placeholder="Type your message here..."
            value={messageDialog.message}
            onChange={(e) => setMessageDialog({ ...messageDialog, message: e.target.value })}
            className="text-field"
            disabled={messageDialog.isSubmitting}
          />
        </div>
        
        <Alert severity="info" className="alert alert-info">
          <Typography variant="body2">
            This message will be sent to {messageDialog.recipientName}. They will be able to reply to you through the messaging system.
          </Typography>
        </Alert>
      </DialogContent>
      
      <div className="dialog-footer">
        <Button 
          className="btn btn-outline"
          onClick={() => setMessageDialog({ ...messageDialog, open: false })}
          disabled={messageDialog.isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          className="btn btn-primary"
          onClick={handleSendMessage}
          disabled={!messageDialog.message.trim() || messageDialog.isSubmitting}
          startIcon={messageDialog.isSubmitting ? <CircularProgress size={20} /> : <Send />}
        >
          {messageDialog.isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </div>
    </Dialog>
  );

  // Snackbar component
  const renderSnackbar = () => (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={closeSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert 
        onClose={closeSnackbar} 
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  if (loading) {
    return (
      <div className="student-dashboard-container">
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <div className="loading-container">
            <CircularProgress size={60} sx={{ mb: 3, color: 'primary.main' }} />
            <Typography variant="h6" className="loading-text">
              Loading Your Dashboard...
            </Typography>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="student-dashboard-container">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Paper className="student-header">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" className="student-title">
                <Dashboard /> Client Dashboard
              </Typography>
              <Typography variant="subtitle1" className="student-subtitle">
                Track your orders, communicate with writers, and manage your assignments
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchDashboardData}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Paper>
        
        {/* Main Tabs */}
        <Paper className="student-tabs-container">
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            className="student-tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                className={`student-tab ${activeTab === index ? 'active' : ''}`}
              />
            ))}
          </Tabs>
          
          {/* Content Panel */}
          <Box className="student-content-panel">
            {activeTab === 0 && renderDashboard()}
            {activeTab === 1 && renderMyOrders()}
            {activeTab === 2 && renderNewOrder()}
            {activeTab === 3 && renderMessages()}
            {activeTab === 4 && renderHistory()}
          </Box>
        </Paper>
      </Container>
      
      {/* Dialogs */}
      {renderOrderDetailsDialog()}
      {renderRevisionDialog()}
      {renderRatingDialog()}
      {renderMessageDialog()}
      
      {/* Snackbar for notifications */}
      {renderSnackbar()}
    </div>
  );
};

export default StudentDashboard;
